import { FC, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "antd";
import type { FormInstance } from "antd";
import ParcelCreateAddressForm from "./ParcelCreateAddressForm";
import {
  useGetMasterDistrict,
  useGetMasterProvince,
  useGetMasterSubDistrict,
} from "hooks/useMasterData";
import { ParcelSearchAgainBox } from "../../manageParcelLayout";
import {
  SEARCH_RECIPIENT_AGAIN_MSG,
  canSaveRecipientPhone,
  isRecipientPhoneOutOfSearch,
  normalizePhoneDigits,
} from "./parcelPhoneSearchUtils";

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
};

export type ParcelCreateAddressModalValues = {
  fullname: string;
  address: string;
  telephone1: string;
  province_id?: number;
  district_id?: number;
  sub_district_id?: number;
  county?: string;
  city?: string;
  state?: string;
  postcode?: string;
};

type ParcelCreateAddressFormValues = {
  address_name: string;
  address_telephone: string;
  line_1: string;
  province_id_ship?: number;
  district_id_ship?: number;
  sub_district_id_ship?: number;
  zip_code_ship?: string;
};

type ParcelCreateAddressModalProps = {
  open: boolean;
  phone: string;
  lastSearchedDigits: string;
  isSearchStale: boolean;
  phoneEditable?: boolean;
  form: FormInstance;
  onClose: () => void;
  onFinish: (values: ParcelCreateAddressModalValues) => void;
  /** ปิด Modal แล้วค้นหาใหม่จากช่องเบอร์ใน Modal */
  onSearchFromModal: (phoneDigits: string) => void;
};

/** Modal สร้างที่อยู่จัดส่ง — layout/footer ตาม MainCreateAgentAddress */
const ParcelCreateAddressModal: FC<ParcelCreateAddressModalProps> = ({
  open,
  phone,
  lastSearchedDigits,
  isSearchStale,
  phoneEditable = false,
  form,
  onClose,
  onFinish,
  onSearchFromModal,
}) => {
  const [provinceId, setProvinceId] = useState(0);
  const [districtId, setDistrictId] = useState(0);

  const watchedModalPhone = Form.useWatch("address_telephone", form) ?? "";

  const { data: masterProvinceData } = useGetMasterProvince();
  const { data: masterDistrictData } = useGetMasterDistrict(provinceId);
  const { data: masterSubDistrictData } = useGetMasterSubDistrict(districtId);

  const phoneOutOfSearch = useMemo(
    () => isRecipientPhoneOutOfSearch(String(watchedModalPhone), lastSearchedDigits),
    [watchedModalPhone, lastSearchedDigits]
  );

  const lockNonPhoneFields = isSearchStale || phoneOutOfSearch;
  const showPhoneMismatchSearch = phoneOutOfSearch && !isSearchStale;

  const canSubmit =
    !isSearchStale &&
    !phoneOutOfSearch &&
    canSaveRecipientPhone(String(watchedModalPhone), lastSearchedDigits);

  useEffect(() => {
    if (open) {
      form.resetFields();
      const initial = normalizePhoneDigits(phone).slice(0, 10);
      form.setFieldsValue({
        address_telephone: initial,
      });
      setProvinceId(0);
      setDistrictId(0);
    }
  }, [open, phone, form]);

  const handlePhoneMismatchSearch = () => {
    const fromForm = form.getFieldValue("address_telephone");
    const digits = normalizePhoneDigits(
      fromForm != null && fromForm !== "" ? String(fromForm) : String(watchedModalPhone)
    );
    if (!digits) {
      return;
    }
    onSearchFromModal(digits);
  };

  const handleFinish = (values: ParcelCreateAddressFormValues) => {
    const telephone1 = normalizePhoneDigits(values.address_telephone).slice(0, 10);
    if (!canSaveRecipientPhone(telephone1, lastSearchedDigits)) {
      return;
    }

    const province = masterProvinceData?.find((p) => p.id === values.province_id_ship);
    const district = masterDistrictData?.find((d) => d.id === values.district_id_ship);
    const subDistrict = masterSubDistrictData?.find(
      (s) => s.id === values.sub_district_id_ship
    );

    onFinish({
      fullname: values.address_name?.trim() ?? "",
      address: values.line_1?.trim() ?? "",
      telephone1,
      province_id: values.province_id_ship,
      district_id: values.district_id_ship,
      sub_district_id: values.sub_district_id_ship,
      county: subDistrict?.name_th ?? "",
      city: district?.name_th ?? "",
      state: province?.name_th ?? "",
      postcode: String(values.zip_code_ship ?? "").trim(),
    });
  };

  return (
    <Modal
      title="สร้างที่อยู่จัดส่ง"
      open={open}
      maskClosable={false}
      onCancel={onClose}
      style={{ top: 20 }}
      destroyOnClose
      footer={[
        <Button key="back" onClick={onClose}>
          ยกเลิก
        </Button>,
        <Button key="submit" type="primary" disabled={!canSubmit} onClick={() => form.submit()}>
          ตกลง
        </Button>,
      ]}
    >
      {isSearchStale ? (
        <ParcelSearchAgainBox style={{ marginBottom: 12 }}>
          {SEARCH_RECIPIENT_AGAIN_MSG}
        </ParcelSearchAgainBox>
      ) : null}

      <Form
        form={form}
        {...formItemLayout}
        onFinish={handleFinish}
        layout="horizontal"
        onValuesChange={(changed) => {
          if (changed.province_id_ship != null) {
            setProvinceId(Number(changed.province_id_ship) || 0);
          }
          if (changed.district_id_ship != null) {
            setDistrictId(Number(changed.district_id_ship) || 0);
          }
        }}
      >
        <ParcelCreateAddressForm
          form={form}
          phoneReadonly={normalizePhoneDigits(phone).slice(0, 10)}
          phoneEditable={phoneEditable && !isSearchStale}
          lockNonPhoneFields={lockNonPhoneFields}
          showPhoneMismatchSearch={showPhoneMismatchSearch}
          onPhoneMismatchSearch={handlePhoneMismatchSearch}
        />
      </Form>
    </Modal>
  );
};

export default ParcelCreateAddressModal;
